import { ExecuteActionRequest, GameState } from "@shared/types";
import { BaseRouteService } from "./BaseRouteService";

interface MinigameResponse {
    count: number;
    isComplete: boolean;
}

type TimeResult = {
    minute: number;
    second: number;
    position: number;
};

type ID = {
    id: boolean;
};

interface KeyTheftState {
    keysStolen: number;
    isComplete: boolean;
    guardPosition: number;
}

/**
 * Service to communicate with game routes of the server application
 */
export class GameRouteService extends BaseRouteService {
    /**
     * Get a type of game state for the current player from the server application
     *
     * @returns A type of game state for the current player
     */
    public async getGameState(): Promise<GameState> {
        return this.getJsonApi<GameState>("game/state");
    }

    /**
     * Execute an action for the current player on the server application
     *
     * @param actionAlias Alias of the action to execute
     * @param objectAliases Aliases of the game objects to execute the action on
     *
     * @returns A type of game state, or `undefined` if the action was unhandled. This can indicate the action requires a second game object to work.
     */
    public async executeAction(
        actionAlias: string,
        objectAliases?: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, ExecuteActionRequest>("game/action", {
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }

    public async updateCount(): Promise<MinigameResponse | undefined> {
        try {
            return await this.postJsonApi("minigame/increment", { });
        }
        catch (error) {
            console.error(error);
            return undefined;
        }
    }

    public async finishCount(): Promise<MinigameResponse | undefined> {
        try {
            return await this.postJsonApi("minigame/finishCount", { });
        }
        catch (error) {
            console.error(error);
            return undefined;
        }
    }

    public async resetCount(): Promise<MinigameResponse | undefined> {
        try {
            return await this.postJsonApi("minigame/reset", { });
        }
        catch (error) {
            console.error(error);
            return undefined;
        }
    }

    public async fetchCurrentCount(): Promise<MinigameResponse | undefined> {
        try {
            return await this.postJsonApi("minigame/check-count", { });
        }
        catch (error) {
            console.error(error);
            return undefined;
        }
    }

    /**
     * Execute an action for updating the risk level
     *
     * @param riskLevel The new risk level to set
     */
    public async updateRisk(riskLevel: number): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState>("game/risk", { riskLevel: riskLevel.toString() });
        }
        catch {
            console.error("error: updateRisk");
            return undefined;
        }
    }

    /**
     * Request a room change for the current player on the server application
     *
     * @param roomAlias Alias of the room to change to
     */
    public async requestRoomChange(roomAlias: string): Promise<void> {
        if (!roomAlias) {
            console.error("error: requestRoomChange - roomAlias is undefined");
            return;
        }
        try {
            await this.postJsonApi("game/room", { alias: roomAlias });
        }
        catch {
            console.error("error: requestRoomChange");
        }
    }

    public async changeDisplay(roomAlias: string): Promise<void> {
        if (!roomAlias) {
            console.error("error: requestRoomChange - roomAlias is undefined");
            return;
        }
        try {
            await this.postJsonApi("game/display", { alias: roomAlias });
        }
        catch {
            console.error("error: requestRoomChange");
        }
    }

    /**
     * Saves the times and ids of the player when a player gets an achievement. This will be saved into the
     * database.
     * @param alias Unique alias of the object the achievement points to
     * @param minute Amount of minutes between the begin of the game and the moment the achievement was achieved.
     * @param second Amount of seconds between the begin of the game and the moment the achievement was achieved.
     */
    public async updateAchievement(alias: string, playerName: string, minute: number, second: number): Promise<void> {
        try {
            await this.postJsonApi("Database/send", { alias: alias, player_name: playerName, minute: minute, second: second });
        }
        catch {
            console.error("error: updateAchievement");
        }
    }

    public async getTimePlayer(alias: string, playerName: string): Promise<[number, number, number]> {
        try {
            const queryResult: TimeResult = await this.postJsonApi("Database/time", { alias: alias, player_name: playerName });
            return [queryResult.minute, queryResult.second, queryResult.position];
        }
        catch {
            console.error("error: getTimePlayer");
            return [-1, -1, -1];
        }
    }

    public async savePlayerName(playerName: string): Promise<boolean> {
        try {
            try {
                const res: ID = await this.postJsonApi("Database/name", { player_name: playerName });
                return res.id;
            }
            catch {
                console.error("error: updateAchievement");
            }
            return false;
        }
        catch {
            return false;
        }
    }

    /**
     * Restart the game for the current player on the server application
     *
     * @returns A new game state after restart
     */
    public async restartGame(): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState>("game/restart", {});
        }
        catch {
            console.error("error: restartGame");
            return undefined;
        }
    }

    /**
     * Fetch the current state of the Key Theft game.
     */
    public async getKeyTheftState(): Promise<KeyTheftState | undefined> {
        try {
            return await this.getJsonApi<KeyTheftState>("Minigame/key-theft/state");
        }
        catch (error) {
            console.error("Error fetching key theft state:", error);
            return undefined;
        }
    }

    /**
     * Send a request to steal a key in the Key Theft game.
     * @param playerPosition The current position of the player
     */
    public async stealKey(playerPosition: number): Promise<KeyTheftState | undefined> {
        try {
            return await this.postJsonApi<KeyTheftState>("Minigame/key-theft/steal", { playerPosition });
        }
        catch (error) {
            console.error("Error stealing key:", error);
            return undefined;
        }
    }

    /**
     * Reset the Key Theft game.
     */
    public async resetKeyTheftGame(): Promise<KeyTheftState | undefined> {
        try {
            return await this.postJsonApi<KeyTheftState>("Minigame/key-theft/reset", {});
        }
        catch (error) {
            console.error("Error resetting key theft game:", error);
            return undefined;
        }
    }
}
